import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Building2, User, FileText, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../utils/apiConfig.js';
import Footer from '../shared/Footer';

const API_BASE_URL = getApiBaseUrl();

const ApplyDistributor = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    business_license: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.contact_person || formData.contact_person.trim().length === 0) {
      newErrors.contact_person = '请输入联系人姓名';
    } else if (formData.contact_person.trim().length < 2) {
      newErrors.contact_person = '联系人姓名至少需要2个字符';
    } else if (formData.contact_person.trim().length > 50) {
      newErrors.contact_person = '联系人姓名不能超过50个字符';
    }

    // 公司名称是可选的
    if (formData.company_name && formData.company_name.trim().length > 100) {
      newErrors.company_name = '公司名称不能超过100个字符';
    }

    // 营业执照是可选的
    if (formData.business_license && formData.business_license.trim().length > 50) {
      newErrors.business_license = '营业执照号不能超过50个字符';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors(prev => ({ ...prev, general: '' }));

    try {
      const response = await fetch(`${API_BASE_URL}/commission/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_name: formData.company_name.trim() || undefined,
          contact_person: formData.contact_person.trim(),
          business_license: formData.business_license.trim() || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setErrors(prev => ({
          ...prev,
          general: data.detail || data.message || '申请失败，请稍后重试'
        }));
        return;
      }

      // 提取邀请码（从消息中提取）
      const codeMatch = data.message && data.message.match(/([A-Z0-9]{8})/);
      if (codeMatch) {
        setInvitationCode(codeMatch[1]);
      }

      setSuccessMessage(data.message || '申请已成功提交');
      setShowSuccess(true);
      setFormData({
        company_name: '',
        contact_person: '',
        business_license: ''
      });
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        general: '网络错误，请稍后重试'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="flex justify-center mb-6">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">申请已提交！</h2>
            <p className="text-gray-600 mb-6">{successMessage}</p>

            {invitationCode && (
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-2">您的邀请码：</p>
                <p className="text-2xl font-bold text-green-600 tracking-widest mb-3">
                  {invitationCode}
                </p>
                <p className="text-xs text-gray-600">
                  请保存好邀请码，审核通过后可将其分享给客户
                </p>
              </div>
            )}

            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                ⏳ 审核通过后，邀请码将被激活，您就可以开始分享邀请码了
              </p>
              <p className="text-sm text-gray-600">
                我们会通过您注册的账户为您的最新消息进行通知
              </p>
            </div>

            <button
              onClick={handleBackToHome}
              className="w-full mt-8 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              返回首页
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <div className="max-w-md w-full">
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-green-600 hover:text-green-700 font-medium mb-4"
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            返回
          </button>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">申请成为分销商</h1>
            <p className="text-gray-600">通过分享邀请码赚取佣金</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 公司名称 */}
            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                公司名称
                <span className="text-xs text-gray-500 ml-1">(可选)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="company_name"
                  type="text"
                  value={formData.company_name}
                  onChange={e => handleInputChange('company_name', e.target.value)}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                    errors.company_name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="如有公司请输入"
                  disabled={isLoading}
                  maxLength={100}
                />
              </div>
              {errors.company_name && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.company_name}
                </p>
              )}
            </div>

            {/* 联系人姓名 */}
            <div>
              <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-2">
                联系人姓名 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="contact_person"
                  type="text"
                  value={formData.contact_person}
                  onChange={e => handleInputChange('contact_person', e.target.value)}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                    errors.contact_person ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="请输入您的姓名"
                  disabled={isLoading}
                  maxLength={50}
                  required
                />
              </div>
              {errors.contact_person && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.contact_person}
                </p>
              )}
            </div>

            {/* 营业执照 */}
            <div>
              <label htmlFor="business_license" className="block text-sm font-medium text-gray-700 mb-2">
                营业执照号
                <span className="text-xs text-gray-500 ml-1">(可选)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="business_license"
                  type="text"
                  value={formData.business_license}
                  onChange={e => handleInputChange('business_license', e.target.value)}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                    errors.business_license ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="如有营业执照请输入"
                  disabled={isLoading}
                  maxLength={50}
                />
              </div>
              {errors.business_license && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.business_license}
                </p>
              )}
            </div>

            {/* 一般错误信息 */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{errors.general}</p>
              </div>
            )}

            {/* 提示信息 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                ℹ️ 申请后系统会自动为您生成一个唯一的8位邀请码，审核通过后即可使用
              </p>
            </div>

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                isLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isLoading ? '提交中...' : '提交申请'}
            </button>
          </form>

          <p className="mt-6 text-xs text-gray-500 text-center">
            审核结果将通过您的账户通知，请保持账户激活
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ApplyDistributor;
